import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import {
  AccountMemberPicker,
  type AccountMember,
} from "@/components/account/AccountMemberPicker";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { AdminPageHeader } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ApiError,
  createBrandMembership,
  deleteBrandMembership,
  fetchBrandMemberships,
  updateBrandMembership,
  type BrandMembership,
  type BrandMembershipCandidate,
  type BrandMembershipRole,
} from "@/lib/brand-memberships-api";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { userAvatarInitial } from "@/lib/user-display";

const CANDIDATE_ROLE_FALLBACK: AccountMember["role"] = "member";

function candidateToAccountMember(candidate: BrandMembershipCandidate): AccountMember {
  const displayName = candidate.display_label?.trim() || null;
  return {
    id: candidate.user_id,
    user_id: candidate.user_id,
    role: CANDIDATE_ROLE_FALLBACK,
    kind: "internal",
    disabled: false,
    user: {
      display_name: displayName,
      display_label: candidate.display_label,
      email: candidate.email,
      phone: candidate.phone,
      avatar_url: null,
      avatar_initial: userAvatarInitial({
        email: candidate.email,
        display_name: displayName,
        avatar_initial: "",
      }),
    },
  };
}

/** 品牌设置 · 成员权限：从账户成员勾选 BrandMembership */
export default function BrandSettingsMembersPage() {
  const { brandId } = useParams();
  const { accountId, brands } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [memberships, setMemberships] = useState<BrandMembership[]>([]);
  const [candidates, setCandidates] = useState<BrandMembershipCandidate[]>([]);
  const [roles, setRoles] = useState<BrandMembershipRole[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [addRole, setAddRole] = useState("member");
  const [removeTarget, setRemoveTarget] = useState<BrandMembership | null>(null);

  const defaultAddRole = useCallback((nextRoles: BrandMembershipRole[]) => {
    return nextRoles.find((r) => r.value === "member")?.value ?? nextRoles[0]?.value ?? "member";
  }, []);

  const load = useCallback(async () => {
    if (!accountId || !brandId) return;
    setLoading(true);
    try {
      const payload = await fetchBrandMemberships(accountId, brandId);
      setMemberships(payload.memberships);
      setCandidates(payload.candidates);
      setRoles(payload.roles);
      setAddRole(defaultAddRole(payload.roles));
      setSelectedIds([]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "加载失败");
    } finally {
      setLoading(false);
    }
  }, [accountId, brandId, defaultAddRole]);

  useEffect(() => {
    void load();
  }, [load]);

  const ungranted = useMemo(() => candidates.filter((c) => !c.granted), [candidates]);
  const pickerMembers = useMemo(
    () => ungranted.map(candidateToAccountMember),
    [ungranted],
  );

  function openAddDialog() {
    if (ungranted.length === 0) {
      toast.info("账户内成员均已加入，或暂无其他成员。新人需先加入账户「成员」。");
      return;
    }
    setSelectedIds([]);
    setAddRole(defaultAddRole(roles));
    setAddOpen(true);
  }

  async function handleAdd() {
    if (!accountId || !brandId || selectedIds.length === 0) {
      toast.error("请先勾选要加入的成员");
      return;
    }
    setBusy(true);
    try {
      for (const userId of selectedIds) {
        await createBrandMembership(accountId, brandId, { user_id: userId, role: addRole });
      }
      toast.success("已加入选中的成员");
      setAddOpen(false);
      setSelectedIds([]);
      await load();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "加入失败");
    } finally {
      setBusy(false);
    }
  }

  async function handleRoleChange(record: BrandMembership, role: string) {
    if (!accountId || !brandId) return;
    setBusy(true);
    try {
      await updateBrandMembership(accountId, brandId, record.id, { role });
      toast.success("角色已更新");
      await load();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "更新失败");
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveConfirm() {
    if (!accountId || !brandId || !removeTarget) return;
    setBusy(true);
    try {
      await deleteBrandMembership(accountId, brandId, removeTarget.id);
      toast.success("已移除");
      setRemoveTarget(null);
      await load();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "移除失败");
    } finally {
      setBusy(false);
    }
  }

  if (!brandId) {
    return <p className="text-sm text-muted-foreground">缺少品牌。</p>;
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="成员权限"
        description={
          brand
            ? `从账户成员勾选谁能参与「${brand.name}」。此处不是独立花名册，不能用邮箱邀请。`
            : "从账户成员勾选谁能参与本品牌。"
        }
        actions={
          <Button type="button" disabled={busy || loading} onClick={openAddDialog}>
            <Plus className="size-4" />
            加入成员
          </Button>
        }
      />

      {loading ? <p className="text-sm text-muted-foreground">加载中…</p> : null}

      {!loading && memberships.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card px-6 py-10 text-center">
          <p className="text-sm text-foreground">还没有品牌成员</p>
          <p className="mt-1 text-xs text-muted-foreground">
            点右上角从账户「成员」中勾选加入。
          </p>
        </div>
      ) : null}

      {!loading && memberships.length > 0 ? (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
          {memberships.map((record) => (
            <li key={record.id} className="flex flex-wrap items-center gap-3 bg-card px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{record.user.display_label}</p>
                <p className="text-xs text-muted-foreground">
                  {[record.user.email, record.user.phone].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              <Select
                value={record.role}
                disabled={busy}
                onValueChange={(value) => void handleRoleChange(record, value)}
              >
                <SelectTrigger className="h-8 w-28" aria-label="角色">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => setRemoveTarget(record)}
              >
                移除
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      <p className="text-xs text-muted-foreground">
        新人需先出现在账户「成员」中；此处不提供邮箱/手机邀请。也可在账户成员的产品授权里统一管理。
        {accountId ? (
          <>
            {" "}
            <Link to={`/accounts/${accountId}/members`} className="text-primary-readable hover:underline">
              打开账户成员 →
            </Link>
          </>
        ) : null}
      </p>

      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          if (busy) return;
          setAddOpen(open);
          if (!open) setSelectedIds([]);
        }}
      >
        <DialogContent className="flex max-h-[min(85vh,640px)] flex-col gap-4 overflow-hidden sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>加入成员</DialogTitle>
            <DialogDescription>勾选能参与本品牌的账户成员，并选择角色。</DialogDescription>
          </DialogHeader>

          <AccountMemberPicker
            mode="multiple"
            members={pickerMembers}
            value={selectedIds}
            onChange={setSelectedIds}
            disabled={busy}
            emptyText="暂无待加入的成员"
            searchPlaceholder="搜索昵称、邮箱、手机…"
            className="min-h-0 flex-1"
          />

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">加入后的角色</Label>
            <Select value={addRole} disabled={busy} onValueChange={setAddRole}>
              <SelectTrigger className="h-8 w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="sm:justify-between">
            <p className="self-center text-xs text-muted-foreground">已选 {selectedIds.length} 人</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" disabled={busy} onClick={() => setAddOpen(false)}>
                取消
              </Button>
              <Button
                type="button"
                disabled={busy || selectedIds.length === 0}
                onClick={() => void handleAdd()}
              >
                {busy ? "加入中…" : "确认加入"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removeTarget != null}
        onOpenChange={(open) => {
          if (!open && !busy) setRemoveTarget(null);
        }}
        title="移除品牌成员"
        description={
          removeTarget
            ? `确定移除「${removeTarget.user.display_label}」对本品牌的权限吗？`
            : null
        }
        confirmLabel="移除"
        tone="destructive"
        confirming={busy}
        onConfirm={() => void handleRemoveConfirm()}
      />
    </div>
  );
}
